import { ChangeEvent, FormEvent, useState } from "react";
import ResetPassword from "../../models/Users/ResetPassword";
import { Box, Button, TextField } from "@mui/material";

interface Props {
  email: string;
  closeForm: () => void;
  passwordReset: (data: ResetPassword) => void;
}

interface ValidationErrors {
  newPassword?: string;
}

export default function PasswordResetForm({
  email,
  closeForm,
  passwordReset,
}: Props) {
  const [userData, setUserData] = useState<ResetPassword>({
    email: email,
    newPassword: "",
  });
  const [errors, setErrors] = useState<ValidationErrors>({});

  function validatePassword(): ValidationErrors {
    const newErrors: ValidationErrors = {};
    
    if (!userData.newPassword) {
      newErrors.newPassword = "Password is required";
    } else {
      if (userData.newPassword.length < 8) {
        newErrors.newPassword = "Password must be at least 8 characters long";
      } else if (!/(?=.*[a-z])/.test(userData.newPassword)) {
        newErrors.newPassword = "Password must contain at least one lowercase letter";
      } else if (!/(?=.*[A-Z])/.test(userData.newPassword)) {
        newErrors.newPassword = "Password must contain at least one uppercase letter";
      } else if (!/(?=.*\d)/.test(userData.newPassword)) {
        newErrors.newPassword = "Password must contain at least one number";
      }
    }
    return newErrors;
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validationErrors = validatePassword();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    passwordReset(userData);
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setUserData({ ...userData, [name]: value });

    if (errors[name as keyof ValidationErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <TextField
        label="New Password"
        name="newPassword"
        value={userData.newPassword}
        onChange={handleInputChange}
        fullWidth
        margin="normal"
        required
        type="password"
        error={!!errors.newPassword}
        helperText={errors.newPassword}
      />
      {/* Password requirements hint */}
      {/* <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
        Password must contain: at least 8 characters, one lowercase letter, one uppercase letter and one number
      </Typography> */}

      <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
        <Button type="submit" variant="contained" color="primary">
          Create
        </Button>
        <Button variant="outlined" onClick={closeForm}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
}
